import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, List, Lock, Shield } from "lucide-react";
import { Card, CardContent, IconChip } from "@/components/ui/card";
import { LogoMark } from "@/components/logo";
import { SobrietyCard } from "@/components/recovery/sobriety-card";
import { StepReadings } from "@/components/recovery/step-readings";
import { PublicFooter, PublicTopBar } from "@/components/public/chrome";
import {
  Band,
  BitList,
  BODY,
  CtaBlock,
  EYEBROW,
  Glimpse,
  INNER,
  SectionHeading,
  type Bit,
} from "@/components/public/section";
import { getFellowships, type FellowshipOption, type StepCitation } from "@/lib/api";
import { useDocumentMeta } from "@/lib/use-document-meta";

// The public Literature page. Ported from the "Happy24" Claude Design project
// (screens/literature.jsx), with two deliberate departures from the canvas:
//
//  1. Its commitments claimed each citation had been checked against a copy
//     before shipping. That is not true — checked_at is NULL on all 35 rows,
//     and AA's ranges ship on recorded provenance instead. Reworded below to
//     what the app actually does.
//  2. Its fellowship list was hardcoded, and still carried the two book
//     references migration 0013 corrected. This page reads
//     GET /api/fellowships instead, so it cannot drift from what the app
//     serves. test/public-pages.test.ts pins both of these.

/* ------------------------- the reading card ------------------------- */

// Illustrative only — the shape of a real row, not a live read. Mirrors what
// production serves for Step 3 (migrations 0012 and 0013): AA's range is
// edition-independent and verified; NA's is chapter-level while the edition is
// still being checked against a copy. Update these when 0013's successor
// changes the underlying data, so the page keeps showing the truth.
const AA_CITATION: StepCitation = {
  fellowship: "aa",
  short: "AA",
  citation: "Big Book, pp. 60–63",
  citation_note: null,
  // Production also carries a "Third Step Prayer" row here; left off so each
  // state on this page shows exactly one card and the difference between the
  // three is the only thing moving.
  also_citation: null,
  also_note: null,
  edition: "any edition — the first 164 pages are unchanged since 1939",
  verified: true,
};

const NA_CITATION: StepCitation = {
  fellowship: "na",
  short: "NA",
  citation: "Basic Text — Step Three",
  citation_note: "Chapter reference. Find the page in your own copy.",
  also_citation: null,
  also_note: null,
  edition: "Basic Text, 6th edition (2008)",
  verified: false,
};

function ReadingState({
  label,
  citations,
  caption,
  note,
}: {
  label: string;
  citations: StepCitation[];
  caption: string;
  note: string;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <p className={EYEBROW}>{label}</p>
      {/* The real component, given synthetic citations — so this section shows
          what a user actually sees rather than a drawing of it. */}
      <Glimpse caption={caption}>
        <StepReadings citations={citations} />
      </Glimpse>
      <p className="text-[13px] leading-[1.55] text-muted-foreground">{note}</p>
    </div>
  );
}

/* ---------------------------- your words ---------------------------- */

/** A milestone card plus the sentence explaining who gets that word. */
function TermRow({
  label,
  word,
  who,
  startDate,
}: {
  label: string;
  word: string;
  who: string;
  startDate: Date;
}) {
  return (
    <div className="flex flex-col gap-2">
      <SobrietyCard startDate={startDate} label={label} />
      <p className="text-[13px] leading-[1.55] text-muted-foreground">
        Says <strong className="font-semibold text-foreground">{word}</strong> everywhere
        it matters. {who}
      </p>
    </div>
  );
}

/* -------------------------- what you can pick -------------------------- */

function FellowshipRow({ f }: { f: FellowshipOption }) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3.5 p-4">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[13px] font-bold text-primary">
          {f.short}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-foreground">{f.name}</p>
          <p className="mt-[3px] text-sm leading-[1.5] text-muted-foreground">{f.books}</p>
          <p className="mt-[3px] text-[13px] text-muted-foreground">
            Milestone language: {f.term.adj}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function FellowshipSkeleton() {
  return (
    <div className="flex flex-col gap-2.5" aria-hidden>
      {[0, 1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardContent className="flex items-start gap-3.5 p-4">
            <span className="h-10 w-10 shrink-0 rounded-full bg-muted" />
            <div className="flex-1 space-y-2 py-1">
              <div className="h-3 w-1/3 rounded bg-muted" />
              <div className="h-3 w-2/3 rounded bg-muted" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** The always-true row: picking nothing is a choice the app supports. */
function NoFellowshipRow() {
  return (
    <Card>
      <CardContent className="flex items-start gap-3.5 p-4">
        <IconChip icon={BookOpen} tint="primary" />
        <div className="flex-1">
          <p className="text-[15px] font-semibold text-foreground">No fellowship at all</p>
          <p className="mt-[3px] text-sm leading-[1.5] text-muted-foreground">
            Your own reading, whatever it is. The app doesn't need to know, and never
            asks again.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function SupportedList() {
  const [fellowships, setFellowships] = useState<FellowshipOption[] | null>(null);
  // Loaded / failed are deliberately different from loading: a failed fetch
  // renders the section without the list rather than an error box. The page's
  // meaning is in the copy around it, not in the roster.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    getFellowships()
      .then((f) => live && setFellowships(f))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  if (failed) return <NoFellowshipRow />;
  if (!fellowships) return <FellowshipSkeleton />;

  return (
    <div className="flex flex-col gap-2.5">
      {fellowships.map((f) => (
        <FellowshipRow key={f.id} f={f} />
      ))}
      <NoFellowshipRow />
    </div>
  );
}

/* --------------------------- where the line is --------------------------- */

const NEVER: Bit[] = [
  {
    icon: BookOpen,
    t: "We never reproduce anyone's literature",
    d: "A reading is a title and a page range. Not one line of any fellowship's text appears in the app.",
  },
  {
    icon: Shield,
    t: "We use no fellowship's name as a badge",
    d: "No logos, no marks, no claim of partnership. Names appear only so you can recognise your own book.",
  },
  {
    icon: List,
    t: "We don't invent page numbers",
    // Reworded from the canvas, which claimed more than the data supports.
    // Overstating this on the page about not overstating things would be a
    // poor place to start.
    d: "Every citation records where it came from, and where a range hasn't been confirmed against a copy, the app cites the chapter instead and says so.",
  },
  {
    icon: Lock,
    t: "The AI never fills the gap",
    d: "It can only cite from the literature you selected. If you selected nothing, it cites nothing, and it never falls back to someone else's book.",
  },
];

/* ------------------------------- the page ------------------------------- */

export default function Literature() {
  // Keep this in step with PUBLIC_PAGES in worker/public-pages.ts: that one is
  // what crawlers read, this one is what the tab, the bookmark and the screen
  // reader announce.
  useDocumentMeta({
    title: "Which literature you use — Happy24",
    description:
      "Happy24 cites the book you actually read: AA, NA, CA, GA, OA, more than one, or none at all. Citations only — never a line of anyone's literature.",
  });

  // Fixed offsets from today rather than a hardcoded date, so the demo cards
  // read sensibly next year instead of counting backwards.
  const dates = useMemo(() => {
    const back = (days: number) => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - days);
      return d;
    };
    return { sober: back(874), clean: back(413), abstinent: back(96) };
  }, []);

  return (
    <div className="min-h-dvh bg-background font-sans">
      <PublicTopBar />

      <main>
        {/* Hero */}
        <Band>
          <div className={`${INNER} py-12 min-[900px]:py-20`}>
            <div className="min-[900px]:max-w-[720px]">
              <p className={EYEBROW}>Step work</p>
              <h1 className="mb-3.5 mt-2.5 text-3xl font-bold leading-[1.2] text-foreground [text-wrap:pretty] min-[900px]:text-[40px]">
                Which literature you use.
              </h1>
              <p className={`${BODY} mb-3 text-base`}>
                Happy24 asks one optional question about your step work: which book you
                read from. Not which fellowship you belong to, and not what you believe.
                You can answer it, change it, or leave it alone entirely.
              </p>
              <p className={`${BODY} mb-7 text-base`}>
                The answer does two things and nothing else. It decides which reading
                your steps cite, and which word the app uses for your time.
              </p>
              <CtaBlock />
            </div>
          </div>
        </Band>

        {/* The reading card */}
        <Band secondary>
          <div className={`${INNER} py-10 min-[900px]:py-16`}>
            <SectionHeading
              eyebrow="The reading card"
              title="Three ways this can go, and none of them is wrong."
            />
            <div className="flex flex-col gap-7 min-[900px]:grid min-[900px]:grid-cols-3 min-[900px]:items-start min-[900px]:gap-8">
              <ReadingState
                label="If you choose nothing"
                citations={[]}
                caption="Step 3, with no literature selected."
                note="A first-class state, not a broken one. Plenty of people work the steps on their own, with a therapist, or from a book of their own."
              />
              <ReadingState
                label="If you choose one"
                citations={[AA_CITATION]}
                caption="Step 3, reading AA literature."
                note="The reading points at your book, by title and page range. The text itself is never shown here."
              />
              <ReadingState
                label="If you choose two"
                citations={[AA_CITATION, NA_CITATION]}
                caption="Step 3, reading both AA and NA literature."
                note="Dual fellowship is common. Both are shown, neither is treated as primary, and the app never picks for you."
              />
            </div>
          </div>
        </Band>

        {/* Your words */}
        <Band>
          <div className={`${INNER} py-10 min-[900px]:py-16`}>
            <SectionHeading
              eyebrow="Your words"
              title="Sober, clean, or abstinent. Yours, not ours."
            />
            <p className={`${BODY} mb-5`}>
              Many people in NA say clean, and find sober wrong for them. The app follows
              your literature rather than asking you to live with someone else's
              vocabulary. It's a small thing in the code and a large one to be on the
              receiving end of.
            </p>
            <div className="flex flex-col gap-6 min-[900px]:grid min-[900px]:grid-cols-3 min-[900px]:items-start min-[900px]:gap-8">
              <TermRow
                label="Sobriety Time"
                word="sober"
                who="For people reading AA or CA literature."
                startDate={dates.sober}
              />
              <TermRow
                label="Clean Time"
                word="clean"
                who="For people reading NA literature."
                startDate={dates.clean}
              />
              <TermRow
                label="Abstinence Time"
                word="abstinent"
                who="For people reading GA or OA literature."
                startDate={dates.abstinent}
              />
            </div>
            <p className="mt-5 text-[13px] leading-[1.55] text-muted-foreground">
              Choose nothing, or choose two that use different words, and the app stays
              neutral: your time is counted plainly, with no adjective attached to it.
              The milestone card is optional in the first place, and simply doesn't
              appear if you never set a date.
            </p>
          </div>
        </Band>

        {/* What you can pick */}
        <Band secondary>
          <div className={`${INNER} py-10 min-[900px]:py-16`}>
            <SectionHeading
              eyebrow="What you can pick"
              title="The literature Happy24 can cite today."
            />
            <p className={`${BODY} mb-5`}>
              Pick as many as apply. Everything below is optional, and all of it is
              editable later in Settings.
            </p>
            <SupportedList />
            <p className="mt-5 text-[13px] leading-[1.55] text-muted-foreground">
              Al-Anon and ACA aren't here. Members of those fellowships are recovering
              from the effects of someone else's addiction, and a good part of this app
              assumes otherwise. We'd rather say that plainly than half-support you.
            </p>
          </div>
        </Band>

        {/* Where the line is */}
        <Band>
          <div className={`${INNER} py-10 min-[900px]:py-16`}>
            <SectionHeading
              eyebrow="Where the line is"
              title="What we will never do with anyone's literature."
            />
            <div className="min-[900px]:grid min-[900px]:grid-cols-2 min-[900px]:items-start min-[900px]:gap-12">
              <BitList bits={NEVER} />
              <p className="mt-6 text-sm leading-[1.6] text-foreground min-[900px]:mt-0">
                Happy24 is an independent reflection tool. It is not affiliated with,
                endorsed by, or connected to Alcoholics Anonymous, Narcotics Anonymous,
                or any other twelve-step fellowship. Fellowship names are used only so
                you can identify your own literature.
              </p>
            </div>
          </div>
        </Band>

        {/* Closing */}
        <Band secondary>
          <div className="px-6 py-14 text-center">
            <div className="mb-4 flex justify-center">
              <LogoMark size={40} />
            </div>
            <h2 className="text-2xl font-semibold text-foreground">One day at a time.</h2>
            <p className={`${BODY} mx-auto mb-6 mt-2.5 max-w-[340px]`}>
              Whatever you read, and whether or not you read anything, the next 24 hours
              are the same 24 hours.
            </p>
            <div className="mx-auto max-w-[340px]">
              <CtaBlock />
            </div>
            <p className="mt-6 text-[13px] text-muted-foreground">
              <Link to="/" className="underline">
                Back to the main page
              </Link>
            </p>
          </div>
        </Band>
      </main>

      <PublicFooter />
    </div>
  );
}
